import { API } from "aws-amplify";

export const isProduction =
  window.location.hostname !== "localhost" &&
  !window.location.hostname.includes("dev.");

// Constants
export const COOKIE_TOKEN = "_llmasaservice_token";
export const COOKIE_EMAIL = "_llmasaservice_email";
export const SESSION_STORAGE_KEY = "llmSessionData";

// Get the environment-specific suffix
export const getEnvSuffix = () => (isProduction ? "_master" : "_dev");

// Cookie functions
export const getCookie = (name: string) => {
  const fullName = name + getEnvSuffix();
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${fullName}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift();
  return null;
};

export const setCookie = (name: string, value: string, days: number) => {
  const fullName = name + getEnvSuffix();
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;

  const sameSite = isProduction ? "None" : "Lax";
  const secure =
    isProduction || window.location.protocol === "https:" ? "Secure;" : "";
  const domain = isProduction ? "domain=.llmasaservice.io;" : "";

  document.cookie = `${fullName}=${value}; ${expires}; ${domain} SameSite=${sameSite}; ${secure} path=/`;
};

export const removeCookie = (name: string) => {
  setCookie(name, "", -1);
};

export const generateCSRFToken = () => {
  const token =
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15);

  localStorage.setItem("_llmasaservice_csrf_token", token);
  return token;
};

export const getCSRFToken = () => {
  return localStorage.getItem("_llmasaservice_csrf_token");
};

// Use this when making authenticated API calls
export const addCSRFHeader = (options: any = {}) => {
  return {
    ...options,
    headers: {
      ...options.headers,
      "X-CSRF-Token": getCSRFToken() || generateCSRFToken(),
    },
  };
};

// Configure global API response interceptor
export const configureTokenExpirationHandler = (
  handleExpiredToken: () => void
) => {
  // Add response interceptor to Amplify API
  const originalRequest = API.get;
  const originalPost = API.post;

  API.get = async function (apiName: string, 
    path: string, 
    init?: { [key: string]: any }) {
    try {
      return await originalRequest.apply(this, [apiName, path, init || {}]);
    } catch (error: any) {
      if (isTokenExpiredError(error)) {
        console.log("Token expired detected in GET request");
        handleExpiredToken();
      }
      throw error;
    }
  };

  API.post = async function ( apiName: string, 
    path: string, 
    init?: { [key: string]: any }) {
    try {
      return await originalPost.apply(this, [apiName, path, init || {}]);
    } catch (error: any) {
      if (isTokenExpiredError(error)) {
        console.log("Token expired detected in POST request");
        handleExpiredToken();
      }
      throw error;
    }
  };
};

// Check if error is related to expired token
function isTokenExpiredError(error: any): boolean {
  return (
    error?.response?.status === 401 ||
    error?.message?.includes("expired") ||
    error?.response?.data?.error?.includes("expired") ||
    error?.response?.data?.message?.includes("expired")
  );
}
